<div class="py-4 px-8 bg-gray-200 min-h-screen w-full overflow-x-hidden">
  <div *ngIf="successMessage" class="mb-3 px-4 py-2 rounded bg-green-50 border border-green-200 text-green-800 text-sm">{{ successMessage }}</div>
  <div *ngIf="errorMessage" class="mb-3 px-4 py-2 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{{ errorMessage }}</div>

  <div *ngIf="loading" class="text-gray-500 text-sm py-4">Loading referral details...</div>

  <div *ngIf="referral && !loading" class="flex flex-col gap-6">
    <!-- Basic Details Section -->
    <div class="rounded-lg overflow-hidden shadow-md bg-white">
      <div class="flex items-center justify-between bg-[#00358E] py-3 px-8 border-top-section">
        <p class="text-white font-semibold">Referral Details</p>
        <div class="flex items-center gap-2">
          <button
            *ngIf="!isDetailsEditMode && canEditReferral()"
            type="button"
            (click)="onEditDetails()"
            class="bg-white text-[#00358E] px-4 py-2 rounded-full font-semibold flex items-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
            </svg>
            Edit Referral
          </button>
          <ng-container *ngIf="isDetailsEditMode">
            <button
              type="button"
              (click)="onCancelDetailsEdit()"
              [disabled]="isSaving"
              class="px-4 py-1.5 bg-white text-[#00358E] rounded-full text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="onSaveDetails()"
              [disabled]="isSaving"
              class="px-4 py-1.5 bg-white text-[#00358E] rounded-full text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {{ isSaving ? 'Saving...' : 'Save' }}
            </button>
          </ng-container>
        </div>
      </div>

      <div class="py-6 px-8">
        <div class="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          <div class="min-w-0">
            <label class="text-black font-medium text-sm">Referral Number</label>
            <p class="font-light break-words">{{ referral.refNumber }}</p>
          </div>

          <div class="min-w-0">
            <label class="text-black font-medium text-sm">Type of Referral</label>
            <p *ngIf="!isDetailsEditMode" class="font-light break-words">{{ referral.typeOfReferral }}</p>
            <select *ngIf="isDetailsEditMode" [(ngModel)]="editModel!.typeOfReferral"
              class="w-full border rounded px-3 py-3 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400"
              [ngClass]="{ 'ring-1 ring-red-500 bg-red-50': isDetailsFieldInvalid('typeOfReferral') }">
              <option [ngValue]="null">Select Type Of Referral</option>
              <option *ngFor="let option of typeOfReferralOptions" [ngValue]="option">{{ option }}</option>
            </select>
          </div>

          <div class="min-w-0">
            <label class="text-black font-medium text-sm">Date Requested</label>
            <p *ngIf="!isDetailsEditMode" class="font-light break-words">{{ referral.dateRequested }}</p>
            <input *ngIf="isDetailsEditMode" type="date" [(ngModel)]="editModel!.dateRequested"
              class="w-full border rounded px-3 py-3 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          <div class="min-w-0">
            <label class="text-black font-medium text-sm">Vetting Date</label>
            <p *ngIf="!isDetailsEditMode" class="font-light break-words">{{ referral.vettingDate }}</p>
            <input *ngIf="isDetailsEditMode" type="date" [(ngModel)]="editModel!.vettingDate"
              class="w-full border rounded px-3 py-3 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          <div class="min-w-0">
            <label class="text-black font-medium text-sm">Claimant Name</label>
            <p *ngIf="!isDetailsEditMode" class="font-light break-words">{{ referral.claimantName }}</p>
            <input *ngIf="isDetailsEditMode" type="text" [(ngModel)]="editModel!.claimantName"
              class="w-full border rounded px-3 py-3 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>

          <div class="min-w-0">
            <label class="text-black font-medium text-sm">Claim Number</label>
            <p *ngIf="!isDetailsEditMode" class="font-light break-words">{{ referral.claimNumber }}</p>
            <input *ngIf="isDetailsEditMode" type="text" [(ngModel)]="editModel!.claimNumber"
              class="w-full border rounded px-3 py-3 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400"
              [ngClass]="{ 'ring-1 ring-red-500 bg-red-50': isDetailsFieldInvalid('claimNumber') }" />
          </div>

          <div class="min-w-0">
            <label class="text-black font-medium text-sm">Vendor Name</label>
            <p *ngIf="!isDetailsEditMode" class="font-light break-words">{{ referral.vendorName }}</p>
            <ng-container *ngIf="isDetailsEditMode">
              <kendo-combobox
                [data]="vendorNameOptions"
                [value]="vendorNameInput"
                (valueChange)="vendorNameInput = $event"
                [filterable]="true"
                [suggest]="true"
                [allowCustom]="true"
                [kendoDropDownFilter]="{ operator: 'contains' }"
                placeholder="Select or type a vendor name"
                class="w-full">
              </kendo-combobox>
              <span
                *ngIf="isNewVendor"
                class="text-xs text-amber-600 mt-1 block"
                title="This vendor will be added to the config when you save"
              >
                New vendor — will be created and attached.
              </span>
            </ng-container>
          </div>
        </div>
      </div>
    </div>

    <!-- Services Section -->
    <div class="rounded-lg overflow-hidden shadow-md bg-white">
      <div class="flex items-center justify-between bg-[#00358E] py-3 px-8 border-top-section">
        <p class="text-white font-semibold">Services</p>
        <div class="flex items-center gap-2">
          <button
            *ngIf="!isServicesEditMode && canEditReferral()"
            type="button"
            (click)="onEditServices()"
            class="bg-white text-[#00358E] px-4 py-2 rounded-full font-semibold flex items-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
            </svg>
            Edit Services
          </button>
          <ng-container *ngIf="isServicesEditMode">
            <button
              type="button"
              (click)="onCancelServicesEdit()"
              [disabled]="isSaving"
              class="px-4 py-1.5 bg-white text-[#00358E] rounded-full text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="onSaveServices()"
              [disabled]="isSaving"
              class="px-4 py-1.5 bg-white text-[#00358E] rounded-full text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {{ isSaving ? 'Saving...' : 'Save' }}
            </button>
          </ng-container>
        </div>
      </div>

      <div class="py-6 px-8">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b-2 border-gray-200">
              <th class="text-left py-2 px-3 font-semibold text-gray-700">Service Type</th>
              <th class="text-left py-2 px-3 font-semibold text-gray-700">Action</th>
              <th class="text-left py-2 px-3 font-semibold text-gray-700">Service Data ID</th>
              <th class="text-left py-2 px-3 font-semibold text-gray-700">Comments</th>
              <th *ngIf="isServicesEditMode" class="w-20 text-center py-2 px-3 font-semibold text-gray-700">Remove</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let svc of services; let i = index" class="border-b border-gray-100">
              <td class="py-2 px-3">
                <span *ngIf="!isServicesEditMode" class="font-light">{{ svc.serviceType }}</span>
                <select *ngIf="isServicesEditMode" [(ngModel)]="svc.serviceType"
                  class="w-full border rounded px-3 py-2 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <option *ngFor="let opt of serviceTypeOptions" [ngValue]="opt">{{ opt }}</option>
                </select>
              </td>
              <td class="py-2 px-3">
                <span *ngIf="!isServicesEditMode" class="font-light">{{ svc.action }}</span>
                <select *ngIf="isServicesEditMode" [(ngModel)]="svc.action"
                  class="w-full border rounded px-3 py-2 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <option *ngFor="let opt of actionOptions" [ngValue]="opt">{{ opt }}</option>
                </select>
              </td>
              <td class="py-2 px-3">
                <span *ngIf="!isServicesEditMode" class="font-light">{{ svc.serviceDataID }}</span>
                <input *ngIf="isServicesEditMode" type="text" [(ngModel)]="svc.serviceDataID"
                  class="w-full border rounded px-3 py-2 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </td>
              <td class="py-2 px-3">
                <span *ngIf="!isServicesEditMode" class="font-light">{{ svc.comments }}</span>
                <input *ngIf="isServicesEditMode" type="text" [(ngModel)]="svc.comments"
                  class="w-full border rounded px-3 py-2 text-sm font-light focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </td>
              <td *ngIf="isServicesEditMode" class="w-20 text-center py-2 px-3">
                <button type="button" (click)="onRemoveService(i)"
                  class="text-red-600 hover:bg-red-50 rounded px-2 py-1 text-sm font-medium">✕</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div *ngIf="isServicesEditMode" class="mt-3">
          <button type="button" (click)="onAddService()"
            class="bg-[#00358E] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#002766] flex items-center gap-1">
            + Add New Service
          </button>
        </div>

        <p *ngIf="services.length === 0 && !isServicesEditMode" class="text-gray-500 text-sm py-2">
          No services found for this referral.
        </p>
      </div>
    </div>
  </div>
</div>
